
import { supabase } from "@/integrations/supabase/client";
import { ClothingItem, ClothingAuditNote } from "../types";
import { mapDbItemToClothingItem, mapClothingItemToDb } from "../utils/clothingMappers";
import { toast } from "sonner";

// Only fetch columns needed by mapDbItemToClothingItem — avoids pulling large
// text blobs (notes, transcript_notes) that can make responses exceed 100 MB.
export const CLOTHING_ITEM_SELECT_COLUMNS =
  'id, name, brand, color, category, subcategory, subcategories, status, store_url, description, image_url, user_id, favorite, lifestyle_tags, dominant_hex_colors, created_at, uploaded_by, processing_status';

// The stylist's annotations on a set of pieces (clothing_audit_notes), as a query of their
// own rather than embedded under clothing_items: the closet must load even when the notes
// cannot, and PostgREST can only embed once the FK is in its schema cache. RLS scopes the
// rows — a customer gets only their own visible notes, a shared-closet viewer none, staff
// everything (the mapper then drops private ones so admin view-as matches the customer's
// view). Ids go up in chunks so a large closet never overruns the URL. `*` rather than a
// column list so the query is indifferent to whether customer_visible exists yet.
const AUDIT_NOTES_CHUNK = 100;

export const fetchAuditNotesForItems = async (itemIds: string[]): Promise<ClothingAuditNote[]> => {
  const notes: ClothingAuditNote[] = [];
  for (let i = 0; i < itemIds.length; i += AUDIT_NOTES_CHUNK) {
    const chunk = itemIds.slice(i, i + AUDIT_NOTES_CHUNK);
    const { data, error } = await supabase
      .from('clothing_audit_notes')
      .select('*')
      .in('clothing_item_id', chunk);
    if (error) throw error;
    notes.push(...((data ?? []) as ClothingAuditNote[]));
  }
  return notes;
};

export const fetchUserClothingItems = async (userId: string): Promise<ClothingItem[]> => {
  try {
    const { data, error } = await supabase
      .from('clothing_items')
      .select(CLOTHING_ITEM_SELECT_COLUMNS)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Supabase error when fetching clothing items:', error);
      throw error;
    }

    const rows = data ?? [];

    // Notes are a garnish: if they fail, the closet still loads, just without them.
    const notesByItem: Record<string, ClothingAuditNote[]> = {};
    try {
      for (const note of await fetchAuditNotesForItems(rows.map((r) => r.id))) {
        (notesByItem[note.clothing_item_id] ??= []).push(note);
      }
    } catch (notesError) {
      console.error('Failed to load stylist notes for closet items:', notesError);
    }

    return rows.map((row) => mapDbItemToClothingItem({ ...row, clothing_audit_notes: notesByItem[row.id] ?? [] }));
  } catch (error) {
    console.error('Error fetching clothing items:', error);
    toast.error('Failed to load closet items');
    throw error;
  }
};

/**
 * Read specific items belonging to any customer, for staff surfaces (the stylist message thread, the task
 * board). Authorization is the "Stylists can view all clothing items" RLS policy
 * (`is_admin_or_stylist(auth.uid())`), not a client-side profile check — unlike fetchClothingItemsByIds
 * below, whose is_admin check throws for a stylist who isn't also an admin and leaves them with an empty grid.
 */
export const fetchClothingItemsByIdsForStaff = async (itemIds: string[]): Promise<ClothingItem[]> => {
  if (itemIds.length === 0) return [];
  const { data, error } = await supabase
    .from('clothing_items')
    .select(CLOTHING_ITEM_SELECT_COLUMNS)
    .in('id', itemIds);
  if (error) {
    console.error('Supabase error when fetching clothing items for staff:', error);
    throw error;
  }
  return (data ?? []).map(mapDbItemToClothingItem);
};

export const fetchClothingItemsByIds = async (itemIds: string[], adminUserId: string): Promise<ClothingItem[]> => {
  try {
    
    // Check if user has admin privileges
    const { data: profile } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', adminUserId)
      .single();
    
    if (!profile?.is_admin) {
      throw new Error('User is not admin, cannot access all clothing items');
    }
    
    if (itemIds.length === 0) {
      return [];
    }
    
    const { data, error } = await supabase
      .from('clothing_items')
      .select(CLOTHING_ITEM_SELECT_COLUMNS)
      .in('id', itemIds)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error('Supabase error when fetching clothing items by IDs:', error);
      throw error;
    }
    
    return data ? data.map(mapDbItemToClothingItem) : [];
  } catch (error) {
    console.error('Error fetching clothing items by IDs:', error);
    throw error;
  }
};

export const addClothingItem = async (item: Omit<ClothingItem, "id">, userId: string): Promise<ClothingItem> => {
  try {
    const dbItem = mapClothingItemToDb(item, userId);

    // Explicitly handle lifestyle_tags for new items
    if (item.lifestyle_tags && item.lifestyle_tags.length > 0) {
      dbItem.lifestyle_tags = item.lifestyle_tags;
    }

    // Explicitly handle dominant_hex_colors for new items
    if (item.dominant_hex_colors && item.dominant_hex_colors.length > 0) {
      dbItem.dominant_hex_colors = item.dominant_hex_colors;
    } else {
      console.warn('⚠️ No dominant_hex_colors provided for new item');
    }

    const { data, error } = await supabase
      .from('clothing_items')
      .insert(dbItem)
      .select()
      .single();
      
    if (error) throw error;
    
    const newItem = mapDbItemToClothingItem(data);
    toast.success("Item added to your closet!");
    return newItem;
  } catch (error) {
    console.error('Error adding item:', error);
    toast.error('Failed to add item to your closet');
    throw error;
  }
};

export const updateClothingItem = async (id: string, updates: Partial<ClothingItem>, userId: string): Promise<boolean> => {
  try {
    
    // Check if the current user is an admin
    const { data: profileData } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', userId)
      .single();
    
    const isAdmin = profileData?.is_admin || false;
    
    // Convert ClothingItem updates to database format
    const dbUpdates: Record<string, any> = {};
    
    if ('name' in updates) dbUpdates.name = updates.name;
    if ('brand' in updates) dbUpdates.brand = updates.brand;
    if ('color' in updates) dbUpdates.color = updates.color;
    if ('category' in updates) dbUpdates.category = updates.category;
    if ('subcategory' in updates) dbUpdates.subcategory = updates.subcategory;
    if ('subcategories' in updates) dbUpdates.subcategories = updates.subcategories;
    if ('status' in updates) dbUpdates.status = updates.status;
    if ('storeUrl' in updates) dbUpdates.store_url = updates.storeUrl;
    if ('description' in updates) dbUpdates.description = updates.description;
    if ('favorite' in updates) dbUpdates.favorite = updates.favorite;
    
    // Handle lifestyle_tags explicitly and ensure it's an array
    if ('lifestyle_tags' in updates) {
      // Make sure lifestyle_tags is handled as an array
      if (Array.isArray(updates.lifestyle_tags)) {
        dbUpdates.lifestyle_tags = updates.lifestyle_tags;
      } else if (updates.lifestyle_tags === null || updates.lifestyle_tags === undefined) {
        dbUpdates.lifestyle_tags = [];
      } else {
        // If somehow it's not in the expected format, log and provide empty array
        console.error('Unexpected lifestyle_tags format:', updates.lifestyle_tags);
        dbUpdates.lifestyle_tags = [];
      }

    }

    // Handle dominant_hex_colors explicitly and ensure it's an array
    if ('dominant_hex_colors' in updates) {
      if (Array.isArray(updates.dominant_hex_colors)) {
        dbUpdates.dominant_hex_colors = updates.dominant_hex_colors;
      } else if (updates.dominant_hex_colors === null || updates.dominant_hex_colors === undefined) {
        dbUpdates.dominant_hex_colors = [];
      } else {
        console.error('Unexpected dominant_hex_colors format:', updates.dominant_hex_colors);
        dbUpdates.dominant_hex_colors = [];
      }

    }
    
    // Handle images array properly
    if ('images' in updates && updates.images) {
      // Store the first image as primary image_url
      dbUpdates.image_url = updates.images[0] || '';
      
      // Log the image URLs being saved
    }
    
    
    // Perform the update - if admin, don't filter by user_id
    let query = supabase
      .from('clothing_items')
      .update(dbUpdates as any)
      .eq('id', id);
    
    // Only filter by user_id if not admin
    if (!isAdmin) {
      query = query.eq('user_id', userId);
    }
    
    const { error, data } = await query.select();
      
    if (error) {
      console.error('Supabase error when updating item:', error);
      throw error;
    }
    
    if (data && data.length === 0) {
      console.error('No item found to update - either item does not exist or user lacks permission');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error updating item:', error);
    toast.error('Failed to update item');
    throw error;
  }
};

export const toggleItemFavorite = async (id: string, favoriteStatus: boolean, userId: string): Promise<boolean> => {
  try {
    
    const { error } = await supabase
      .from('clothing_items')
      .update({ favorite: favoriteStatus })
      .eq('id', id)
      .eq('user_id', userId);
      
    if (error) {
      console.error('Supabase error when updating favorite status:', error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error updating favorite status:', error);
    return false;
  }
};

export const deleteClothingItem = async (id: string, userId: string): Promise<boolean> => {
  try {
    
    const { error } = await supabase
      .from('clothing_items')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);
      
    if (error) {
      console.error('Supabase error when deleting item:', error);
      throw error;
    }
    
    toast.success('Item deleted successfully');
    return true;
  } catch (error) {
    console.error('Error deleting item:', error);
    toast.error('Failed to delete item from your closet');
    return false;
  }
};
